import { z } from 'zod';

import { ComponentValidator, ComponentPayloadValidator } from '../Component';

import { IsolationVoltage } from '../fields/IsolationVoltage';

import { AbstractPort, Port } from '../fields/AbstractPort';

import { synchronize } from '../helpers';

import { ComponentDefinition } from '../../types/ComponentDefinition';

const port = AbstractPort.extend({
    AC: Port.AC.extend({
        controlMethods: Port.controlMethods.validator,
    }).default({}),
    powerFlowDirection: Port.powerFlowDirection.validator.default('bidirectional'),
}).default({
    powerFlowDirection: 'bidirectional',
});

const electrical = z
    .object({
        ports: z.tuple([port, port, port]).default([undefined, undefined, undefined]),
        standards: z.array(z.string()).default([]),
        isolationVoltage: IsolationVoltage.validator,
    })
    .default({});

const TransferSwitchBase = z.object({
    type: z.literal('transferSwitch').default('transferSwitch'),
    electrical: synchronize(electrical, {
        'ports.0.AC': ['ports.1.AC', 'ports.2.AC'],
        'ports.0.DC': ['ports.1.DC', 'ports.2.DC'],
    }),
});

export const TransferSwitchValidator = ComponentValidator.merge(TransferSwitchBase);
export const TransferSwitchPayloadValidator = ComponentPayloadValidator.merge(TransferSwitchBase);

export const TransferSwitchDefinition: ComponentDefinition = {
    type: 'transferSwitch',
    name: 'Transfer switch',
    plural: 'Transfer switches',
    indicator: 'Q',
    hasDynamicNumberOfPorts: false,
    interoperabilityDataModel: true,
    ports: {
        min: 3,
        max: 3,
        powerFlowDirections: ['input', 'output', 'bidirectional'],
        displayAsSinglePort: true,
    },
    liveData: {
        enabled: true,
        showGraphs: true,
    },
    validators: {
        component: TransferSwitchValidator,
        payload: TransferSwitchPayloadValidator,
    },
};

export type TransferSwitch = z.infer<typeof TransferSwitchValidator>;
export type TransferSwitchPayload = z.infer<typeof TransferSwitchPayloadValidator>;
